import { FEATURES } from "@markfeed/shared";

/** Single source of truth for marketing-page SEO; used by <Seo>, the prerenderer and the sitemap. */
export const SITE_URL = "https://markfeed.site";

/** Shared preview image for WhatsApp, LinkedIn, X and Facebook link cards (apps/web/public). */
export const OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface SeoPage {
  /** Keep under 60 characters so Google doesn't cut it off. */
  title: string;
  /** Keep under 160 characters. */
  description: string;
  priority: number;
  /** Blog posts: publication date (YYYY-MM-DD), used for Article structured data. */
  published?: string;
}

export const SEO_PAGES: Record<string, SeoPage> = {
  "/": {
    title: "Website Feedback Tool for Indian Agencies | MarkFeed",
    description:
      "Stop chasing client feedback on WhatsApp. Clients click on your website to comment, and every change, screenshot and approval stays in one place. Free plan.",
    priority: 1,
  },
  "/features": {
    title: "Features: Website Feedback, Tasks & Approvals | MarkFeed",
    description: "Click-to-comment website feedback, automatic screenshots, a Kanban task board, client approvals and WhatsApp-friendly review links, built for Indian teams.",
    priority: 0.9,
  },
  "/features/website-feedback": {
    title: "Website Feedback Tool: Click to Comment | MarkFeed",
    description: "Collect visual website feedback with element targeting, screenshots, device and browser details. Works with WordPress, Shopify, React and Next.js.",
    priority: 0.9,
  },
  "/features/client-feedback": {
    title: "Client Feedback & Approval Software | MarkFeed",
    description: "Share a no-login review link, let clients comment, resolve and approve pages. Approvals recorded with person, version and timestamp.",
    priority: 0.9,
  },
  ...(FEATURES.mockups
    ? {
        "/features/mockup-feedback": {
          title: "Design & Mockup Feedback: PNG, JPG, PDF | MarkFeed",
          description: "Upload designs and mockups, let clients pin comments on them and turn every comment into a task.",
          priority: 0.8,
        },
      }
    : {}),
  "/features/task-management": {
    title: "Feedback Task Management for Web Agencies | MarkFeed",
    description: "Every piece of client feedback becomes a task: assign, prioritise and move it across Open, In Progress, Awaiting Review and Resolved.",
    priority: 0.8,
  },
  "/for/agencies": {
    title: "Website Feedback Tool for Agencies in India | MarkFeed",
    description: "Manage client feedback across every website project. Client-only access, team assignment, approvals and WhatsApp review requests.",
    priority: 0.9,
  },
  "/for/freelancers": {
    title: "Client Feedback Tool for Freelancers | MarkFeed",
    description: "Send a professional review link instead of WhatsApp screenshots. Record every change request and get approval before the final invoice. From ₹0.",
    priority: 0.9,
  },
  "/for/startups": {
    title: "Website Review Software for Startups | MarkFeed",
    description: "QA and stakeholder feedback on staging with browser, OS and viewport captured automatically. Versions per release, a task per bug.",
    priority: 0.8,
  },
  "/pricing": {
    title: "Pricing: Free, ₹499 and ₹999 Plans | MarkFeed",
    description: "Starter is free forever. Professional ₹499/month for 20 websites, Business ₹999/month for unlimited websites. Pay by UPI, card or net banking.",
    priority: 0.9,
  },
  "/about": {
    title: "About MarkFeed: Made in India for Web Teams",
    description: "We're building the calmest way for agencies, freelancers and clients to review websites together.",
    priority: 0.5,
  },
  "/contact": {
    title: "Contact MarkFeed: Sales & Support",
    description: "Talk to the MarkFeed team about plans, onboarding your agency or anything else.",
    priority: 0.5,
  },
  "/blog": {
    title: "Blog: Client Feedback & Agency Tips | MarkFeed",
    description: "Guides for Indian web agencies and freelancers on collecting client feedback, managing revisions and getting faster approvals.",
    priority: 0.6,
  },
  "/blog/stop-whatsapp-feedback": {
    title: "Why WhatsApp Fails for Website Feedback | MarkFeed",
    description: "Screenshots without context, voice notes nobody can tick off, and changes buried in chat. A simple feedback system for Indian web agencies.",
    priority: 0.6,
    published: "2026-10-11",
  },
  "/blog/client-approval-checklist": {
    title: "Client Approval Checklist for Freelancers | MarkFeed",
    description: "What to agree before the project, the checklist to send before final approval, and how to end the 'one last change' loop after payment.",
    priority: 0.6,
    published: "2026-10-11",
  },
  "/blog/qa-on-staging": {
    title: "Stakeholder QA on Staging, No Spreadsheet | MarkFeed",
    description: "A lightweight review process for startup teams shipping weekly: versions, focused briefs, daily triage, priorities and one clear sign-off.",
    priority: 0.6,
    published: "2026-10-11",
  },
  "/terms": {
    title: "Terms & Conditions | MarkFeed",
    description: "The terms for using MarkFeed: accounts, plans and billing, your content, acceptable use and governing law (India).",
    priority: 0.3,
  },
  "/privacy": {
    title: "Privacy Policy | MarkFeed",
    description: "What personal data MarkFeed collects, how it's used and shared, your rights under India's DPDP Act, and how to contact our Grievance Officer.",
    priority: 0.3,
  },
  "/refund-policy": {
    title: "Refund & Return Policy | MarkFeed",
    description: "No automatic charges, cancel any time, credit for unused time when you upgrade, and full refunds for duplicate or failed payments in 5–7 working days.",
    priority: 0.3,
  },
};
